import * as React from 'react';
import {Breakpoints, css, styled} from '@volkswagen-onehub/components-core';
import {setTopBarHeight} from '../navigation-top-bar-one-hub-helpers';
import {ThemeContext} from 'styled-components';
import {PropsWithDirection} from '../helpers';

type EditorialStageProps = Readonly<{
    media?: JSX.Element;
    overlay?: JSX.Element;
    metaInfo?: JSX.Element;
    copy?: JSX.Element;
    readingTimeBox?: JSX.Element;
    disableOverlayFilter?: boolean; // @deprecated
    disclaimersOverlay?: JSX.Element;
    isInEditor?: boolean;
}>;

const CSS_VAR_EXPANDED_TOPBAR_HEIGHT = '--s2-topbar-height-expanded';
const safeTopBarPadding = css`calc(${props =>
    props.theme.size
        .static200} + var(${CSS_VAR_EXPANDED_TOPBAR_HEIGHT}, 0px));`;

const EditorialStageWrapper = styled.div`
    position: relative;
    /* This has no direct connection to the top bar component, it just sets the
	height of how large the expanded top bar for the current breakpoint is into
	a CSS variable. The CSS variable can be accessed inside this component.
	In the future we could possibly refactor this by somehow making these
	variables globally available.*/
    ${setTopBarHeight(CSS_VAR_EXPANDED_TOPBAR_HEIGHT, true)}
`;

EditorialStageWrapper.displayName = 'EditorialStageWrapper';

const MediaArea = styled.div<{isInEditor?: boolean}>`
    height: 65vh;
    position: relative;

    @media (min-width: ${Breakpoints.b560}px) {
        height: ${props => (props.isInEditor ? '100%' : '100vh')};
        max-height: 100vh;
    }
`;

const CopyWrap = styled.div`
    @media (min-width: ${Breakpoints.b560}px) {
        padding-block-start: ${props => props.theme.size.static370};
    }
`;

const ContentArea = styled.div<{isInEditor?: boolean}>`
    padding: ${props => props.theme.size.static250}
        ${props => props.theme.size.grid002} 0;
    display: flex;
    flex-direction: column;

    @media (min-width: ${Breakpoints.b560}px) {
        min-height: ${props => (props.isInEditor ? '100%' : '100vh')};
        padding: ${safeTopBarPadding} ${props => props.theme.size.grid002}
            ${props => props.theme.size.grid002};
    }
`;

const ContentBoxWrap = styled.div`
    flex: 5;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${props => props.theme.size.static350};

    @media (min-width: ${Breakpoints.b560}px) {
        gap: ${props => props.theme.size.static200};
    }
`;

const DesktopReadingTimeBox = styled.div`
    flex: 1;
    display: none;
    @media (min-width: ${Breakpoints.b560}px) {
        display: initial;
    }
`;

const AbsoluteMedia = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
`;

// Note: same order of layout should be applied for both rtl and ltr
const StyledLayout = styled.div<PropsWithDirection>`
    display: grid;
    grid-template-columns: 1fr;
    margin: auto;
    max-width: 2560px;

    @media (min-width: ${Breakpoints.b560}px) {
        grid-template-columns: [col1-start] ${props =>
                props.direction === 'ltr'
                    ? props.theme.size.grid011
                    : props.theme.size
                          .grid013} [col1-end col2-start] ${props =>
                props.direction === 'ltr'
                    ? props.theme.size.grid013
                    : props.theme.size.grid011} [col2-end];

        & ${MediaArea} {
            grid-column: ${props =>
                props.direction === 'ltr' ? 'col1' : 'col2'};
            grid-row: 1/2;
        }

        & ${ContentArea} {
            grid-column: ${props =>
                props.direction === 'ltr' ? 'col2' : 'col1'};
            grid-row: 1/2;
        }
    }

    @media (min-width: ${Breakpoints.b1600}px) {
        grid-template-columns: [col1-start] ${props => props.theme.size.grid012} [col1-end col2-start] ${props =>
                props.theme.size.grid012} [col2-end];
    }
`;

export const EditorialStage: React.FunctionComponent<
    EditorialStageProps
> = props => {
    const {
        media,
        overlay,
        copy,
        metaInfo,
        readingTimeBox,
        disclaimersOverlay,
        isInEditor
    } = props;

    return (
        <EditorialStageWrapper>
            <ThemeContext.Consumer>
                {value => (
                    <StyledLayout direction={value.direction}>
                        <MediaArea isInEditor={isInEditor}>
                            <AbsoluteMedia>{media}</AbsoluteMedia>

                            {!isInEditor && disclaimersOverlay}
                        </MediaArea>
                        <ContentArea isInEditor={isInEditor}>
                            <ContentBoxWrap>
                                {overlay}
                                {metaInfo}
                                {copy && <CopyWrap>{copy}</CopyWrap>}
                            </ContentBoxWrap>
                            {readingTimeBox && (
                                <DesktopReadingTimeBox>
                                    {readingTimeBox}
                                </DesktopReadingTimeBox>
                            )}
                        </ContentArea>
                    </StyledLayout>
                )}
            </ThemeContext.Consumer>
        </EditorialStageWrapper>
    );
};

export const EditorialStageForAEMEditor: React.FunctionComponent<
    Omit<EditorialStageProps, 'isInEditor'>
> = props => <EditorialStage {...props} isInEditor />;

EditorialStageForAEMEditor.displayName = 'EditorialStageForAEMEditor';
